import React, { useEffect, useState } from "react";
import { MdClose, MdDone, MdOutlineArrowBackIosNew } from "react-icons/md";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ADMIN_PAGE_USERS_AD_LIMIT } from "../../../configs/limits/adLimits";
import { getUsers, verifyUser } from "../../../services/admin-service/adminService";
import { AdminUser } from "../../../types/admin-types/adminTypes";
import useAuthorization from "../../../hooks/useAuthorization";
import { UserRole } from "../../../enums/UserRole";
import { toastError, toastSuccess } from "../../../utils/toast";
import { decodeWithSalt, encodeWithSalt } from "../../../utils/encodeFunctions";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import "../../../styles/css/data-table.css";

function AdminUsersPage() {
  useAuthorization([UserRole.ADMIN, UserRole.ROOT]);
  const userRole = decodeWithSalt(sessionStorage.getItem("role") ?? "");
  const navigate = useNavigate();
  let [searchParams, setSearchParams] = useSearchParams();
  let page = searchParams.get("page") || "1";
  let userType = searchParams.get("userType") || "sellers";
  let [usersList, setUsersList] = useState<AdminUser[]>([]);
  let [isLastPage, setIsLastPage] = useState<boolean>(false);

  async function handleTabClick(isTabChanged: boolean = false, tabLabel: string) {
    setSearchParams({ page: isTabChanged ? "1" : page, userType: tabLabel });

    let payload = {
      limit: ADMIN_PAGE_USERS_AD_LIMIT,
      skip: 0 + (Number(page) - 1) * ADMIN_PAGE_USERS_AD_LIMIT,
    };
    try {
      const userData = await getUsers(tabLabel, payload);
      setUsersList(userData[tabLabel]);
      setIsLastPage(userData.is_last_page);
    } catch (error: any) {
      setUsersList([]);
    }
  }

  async function handleActionBtn(userDetails: AdminUser) {
    let payload = { user_id: userDetails.id, verified: !userDetails.verified };
    try {
      await verifyUser(userType, payload);
      toastSuccess(
        userDetails.verified
          ? `You've rejected the ${userType.slice(0, -1)}`
          : `You've approved the ${userType.slice(0, -1)}`
      );
      setUsersList((prev) =>
        prev.map((user) =>
          user.id === userDetails.id
            ? { ...user, verified: !userDetails.verified }
            : user
        )
      );
    } catch (error: any) {
      toastError(error.response.data.message);
    }
  }

  function handleNext() {
    setSearchParams({ page: (Number(page) + 1).toString(), userType });
  }

  function handlePrevious() {
    if (Number(page) - 1 > 0) {
      setSearchParams({ page: (Number(page) - 1).toString(), userType });
    }
  }

  useEffect(() => {
    handleTabClick(false, userType);
  }, [page]);

  return (
    <div className="data-page">
      <div className="container">
        <div className="data-page__card">
          {/* Header */}
          <div className="data-page__header">
            <div className="data-page__title-group">
              <button
                className="data-page__back-btn"
                onClick={() => navigate("/admin/dashboard")}
              >
                <MdOutlineArrowBackIosNew size={18} />
              </button>
              <div>
                <h4 className="data-page__title">Users</h4>
                <p className="data-page__subtitle">Manage seller and admin accounts</p>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="data-page__tabs">
            <ul className="nav nav-tabs">
              <li className="nav-item cursor-pointer" onClick={() => handleTabClick(true, "sellers")}>
                <a className={"nav-link " + (userType === "sellers" ? "active" : "")}>
                  Sellers
                </a>
              </li>
              <li
                className="nav-item cursor-pointer"
                onClick={() =>
                  userRole === UserRole.ROOT ? handleTabClick(true, "admins") : undefined
                }
              >
                <a
                  className={
                    "nav-link " +
                    (userType === "admins" ? "active " : "") +
                    (userRole === UserRole.ROOT ? "" : "disabled")
                  }
                >
                  Admins
                </a>
              </li>
            </ul>
          </div>

          {/* Table */}
          <div className="data-table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th></th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Status</th>
                  <th>Approval</th>
                </tr>
              </thead>
              <tbody>
                {!usersList?.length ? (
                  <tr>
                    <td colSpan={5} className="data-table-empty">
                      No users found
                    </td>
                  </tr>
                ) : (
                  usersList.map((user, index) => (
                    <tr key={user.id ?? index}>
                      <th scope="row">{index + 1}</th>
                      <td>
                        <Link
                          to={`/seller-profile/${encodeWithSalt(user.id)}`}
                          className="listing-link"
                        >
                          {user.name}
                        </Link>
                      </td>
                      <td style={{ color: "#64748b", fontSize: "0.8125rem" }}>
                        {user.email}
                      </td>
                      <td>
                        {user.verified ? (
                          <span className="status-badge status-badge--approved">
                            <MdDone size={12} style={{ marginRight: 4 }} /> Verified
                          </span>
                        ) : (
                          <span className="status-badge status-badge--pending">
                            <MdClose size={12} style={{ marginRight: 4 }} /> Unverified
                          </span>
                        )}
                      </td>
                      <td>
                        <div className="form-check form-switch mb-0">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            role="switch"
                            checked={user.verified}
                            onChange={() => handleActionBtn(user)}
                            style={{ cursor: "pointer" }}
                          />
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="data-page__pagination">
            <button
              className="data-page__nav-btn"
              disabled={Number(page) === 1}
              onClick={handlePrevious}
            >
              <FaChevronLeft size={11} /> Previous
            </button>
            <span className="data-page__page-info">Page {page}</span>
            <button
              className="data-page__nav-btn"
              disabled={isLastPage}
              onClick={handleNext}
            >
              Next <FaChevronRight size={11} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminUsersPage;
